import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Dropdown } from '@/components/ui/select'
import { Modal } from '@/components/ui/modal'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { apiClient } from '@/lib/api-client'
import { statusLabel } from '@/lib/device-format'

const OPTIONS = [
  { value: 'unavailable', label: 'Unavailable' },
  { value: 'broken', label: 'Broken' },
  { value: 'completely_used', label: 'Completely used' },
  { value: 'eol', label: 'EoL' },
]

interface DeviceStatusModalProps {
  isOpen: boolean
  onClose: () => void
  deviceId: number
  serial: string
  currentStatus?: string
  hasHolder: boolean
  onSuccess: () => void
}

export function DeviceStatusModal({
  isOpen,
  onClose,
  deviceId,
  serial,
  currentStatus,
  hasHolder,
  onSuccess,
}: DeviceStatusModalProps) {
  const [status, setStatus] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!isOpen) return
    setStatus(OPTIONS.some((option) => option.value === currentStatus) ? currentStatus || '' : '')
    setReason('')
  }, [isOpen, currentStatus])

  const mutation = useMutation({
    mutationFn: async () => {
      await apiClient.put(`/devices/${deviceId}/status`, {
        status,
        reason: reason.trim(),
      })
    },
    onSuccess: () => {
      toast.success(`Marked ${statusLabel(status).toLowerCase()}`)
      onSuccess()
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Could not update status')
    },
  })

  const reasonOk = reason.trim().length >= 5

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update status" size="md">
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (!status || !reasonOk || mutation.isPending) return
          mutation.mutate()
        }}
      >
        <p className="text-sm text-muted-foreground">
          Set <span className="font-medium text-foreground">{serial}</span> to unavailable, broken, completely used, or EoL.
          {hasHolder ? ' The current holder will be cleared and the assignment closed.' : ''}
        </p>
        <div className="space-y-2">
          <Label>New status</Label>
          <Dropdown
            value={status}
            onChange={setStatus}
            placeholder="Select a status"
            options={OPTIONS}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="status-reason">Reason</Label>
          <Textarea
            id="status-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this asset being taken out of service?"
            rows={4}
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground">At least 5 characters. This reason stays on the asset.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!status || !reasonOk || mutation.isPending}>
            {mutation.isPending && <LoadingSpinner size="sm" className="mr-2" />}
            Save status
          </Button>
        </div>
      </form>
    </Modal>
  )
}
